/* =============================================================================
   CORE / NUTRITION — Nährwert-/Allergen-Aggregation (S151 ERP Phase A1)
   =============================================================================
   PURE JS-Spiegel der SQL-RPC `menu_item_nutrition` + `ingredient_qty_to_base`
   (Migration phase_113). Identische Formel + Rundung, damit die UI ohne
   Roundtrip rechnen kann und alles testbar ist. Single-Source des Algorithmus —
   bei Änderung BEIDE pflegen (SQL + diese Datei).

   Algorithmus (siehe Migration-Header):
     base_amount := (unit ∈ {null,'base'}) ? quantity
                                           : quantity × namedUnit.gramsOrMl
     grams       := (baseUnit='piece')     ? base_amount × pieceWeightG
                                           : base_amount                  (g/ml)
     nutrient_je_Zeile := grams/100 × ingredient.<nährwert je 100>
     allergens   := Vereinigung (sortiert)   ·  dietary_tags := Schnittmenge (sortiert)
     missing_data:= eine Zutat hat Nährwerte NULL ODER Menge nicht in g/ml auflösbar
   A1: KEINE Verlustfaktoren (yield_* ignoriert).
============================================================================ */

/** EU-14 Hauptallergene (LMIV) — Keys für `ingredients.allergens`. */
export const ALLERGENS_EU14 = [
  { key: 'gluten',   label: 'Glutenhaltiges Getreide' },
  { key: 'crustaceans', label: 'Krebstiere' },
  { key: 'egg',      label: 'Eier' },
  { key: 'fish',     label: 'Fisch' },
  { key: 'peanuts',  label: 'Erdnüsse' },
  { key: 'soy',      label: 'Soja' },
  { key: 'milk',     label: 'Milch/Laktose' },
  { key: 'nuts',     label: 'Schalenfrüchte (Nüsse)' },
  { key: 'celery',   label: 'Sellerie' },
  { key: 'mustard',  label: 'Senf' },
  { key: 'sesame',   label: 'Sesam' },
  { key: 'sulphites', label: 'Schwefeldioxid/Sulfite' },
  { key: 'lupin',    label: 'Lupinen' },
  { key: 'molluscs', label: 'Weichtiere' }
];

/** Diätetische Tags (LMIV-relevant → „Vorschlag, Mensch bestätigt"). */
export const DIETARY_TAGS = [
  { key: 'vegan',        label: 'Vegan' },
  { key: 'vegetarian',   label: 'Vegetarisch' },
  { key: 'gluten_free',  label: 'Glutenfrei' },
  { key: 'lactose_free', label: 'Laktosefrei' }
];

/** EU Big-7 (+ kJ) Nährwert-Felder. camelCase = Storage-Mapping. */
export const NUTRIENT_KEYS = ['energyKcal', 'energyKj', 'fatG', 'fatSaturatedG', 'carbsG', 'sugarG', 'proteinG', 'saltG'];

function _round(x, n) {
  if (x == null || !isFinite(x)) return 0;
  const f = Math.pow(10, n);
  return Math.round((x + Number.EPSILON) * f) / f;
}

/**
 * Menge → Nährwert-Basis (Gramm/ml). Spiegel von `ingredient_qty_to_base`.
 * @param {object} ingredient - { baseUnit, pieceWeightG }
 * @param {number} quantity
 * @param {string|null} unit - 'base' | benannte Einheit
 * @param {Array<{unitName,gramsOrMl}>} [namedUnits] - benannte Einheiten der Zutat
 * @returns {number|null} g/ml, oder null wenn nicht auflösbar
 */
export function qtyToBase(ingredient, quantity, unit, namedUnits = []) {
  if (quantity == null || !isFinite(quantity)) return null;
  if (!ingredient) return null;
  let amount;
  if (unit == null || unit === 'base') {
    amount = quantity;
  } else {
    const nu = (namedUnits || []).find(u => u && String(u.unitName).toLowerCase() === String(unit).toLowerCase());
    if (!nu || nu.gramsOrMl == null) return null;
    amount = quantity * Number(nu.gramsOrMl);
  }
  if (ingredient.baseUnit === 'piece') {
    if (ingredient.pieceWeightG == null) return null;
    return amount * Number(ingredient.pieceWeightG);
  }
  return amount;
}

function _nutrMissing(ing) {
  return ing.energyKcal == null || ing.fatG == null || ing.carbsG == null
      || ing.proteinG == null || ing.saltG == null;
}

/**
 * Aggregiert Nährwerte/Allergene/Tags über die NICHT-optionalen Rezeptzeilen.
 * Spiegel von `menu_item_nutrition`.
 * @param {Array<{ingredientId,quantity,unit,isOptional}>} recipeItems
 * @param {Object<string,object>} ingredientsById - camelCase-Zutaten
 * @param {Object<string,Array>} [namedUnitsByIngredient] - { ingredientId: [{unitName,gramsOrMl}] }
 * @returns {{energyKcal,energyKj,fatG,fatSaturatedG,carbsG,sugarG,proteinG,saltG,allergens,dietaryTags,missingData,ingredientCount}}
 */
export function computeNutrition(recipeItems, ingredientsById, namedUnitsByIngredient = {}) {
  return _aggregate(recipeItems, ingredientsById, namedUnitsByIngredient);
}

/**
 * Kern-Aggregation (intern). Identische Formel + Rundung wie die `lines`-CTE in
 * SQL (A1 menu_item_nutrition / A2 compose_nutrition). Single-Source: sowohl
 * computeNutrition (Gericht) als auch composeNutrition (Bestellung) laufen hier durch.
 */
function _aggregate(recipeItems, ingredientsById, namedUnitsByIngredient = {}) {
  const sum = { energyKcal: 0, energyKj: 0, fatG: 0, fatSaturatedG: 0, carbsG: 0, sugarG: 0, proteinG: 0, saltG: 0 };
  const allergens = new Set();
  const tagLineSets = [];
  let missing = false;
  let count = 0;

  for (const ri of (Array.isArray(recipeItems) ? recipeItems : [])) {
    if (!ri || ri.isOptional) continue;
    const ing = ingredientsById && ingredientsById[ri.ingredientId];
    if (!ing) continue; // wie SQL-JOIN: fehlende/gelöschte Zutat fällt raus
    count++;
    const grams = qtyToBase(ing, ri.quantity, ri.unit, namedUnitsByIngredient[ri.ingredientId] || []);
    if (_nutrMissing(ing) || grams == null) missing = true;
    if (grams != null) {
      const f = grams / 100;
      sum.energyKcal   += f * (ing.energyKcal   ?? 0);
      sum.energyKj     += f * (ing.energyKj     ?? 0);
      sum.fatG         += f * (ing.fatG         ?? 0);
      sum.fatSaturatedG += f * (ing.fatSaturatedG ?? 0);
      sum.carbsG       += f * (ing.carbsG       ?? 0);
      sum.sugarG       += f * (ing.sugarG       ?? 0);
      sum.proteinG     += f * (ing.proteinG     ?? 0);
      sum.saltG        += f * (ing.saltG        ?? 0);
    }
    (ing.allergens || []).forEach(a => allergens.add(a));
    tagLineSets.push(new Set(ing.dietaryTags || []));
  }

  // dietary_tags = Schnittmenge über ALLE (nicht-optionalen) Zeilen
  let dietaryTags = [];
  if (count > 0) {
    const tally = new Map();
    tagLineSets.forEach(set => set.forEach(t => tally.set(t, (tally.get(t) || 0) + 1)));
    dietaryTags = [...tally.entries()].filter(([, c]) => c === count).map(([t]) => t).sort();
  }

  return {
    energyKcal: _round(sum.energyKcal, 1),
    energyKj: _round(sum.energyKj, 1),
    fatG: _round(sum.fatG, 1),
    fatSaturatedG: _round(sum.fatSaturatedG, 1),
    carbsG: _round(sum.carbsG, 1),
    sugarG: _round(sum.sugarG, 1),
    proteinG: _round(sum.proteinG, 1),
    saltG: _round(sum.saltG, 2),
    allergens: [...allergens].sort(),
    dietaryTags,
    missingData: missing,
    ingredientCount: count
  };
}

// =============================================================================
// S152 ERP Phase A2 — Modulare Komposition (Nährwerte je echter Bestellung)
// =============================================================================
// Spiegel der SQL-RPC `compose_nutrition` (Migration phase_114). VOLL MODULAR:
//   Basis  = Rezept des Gerichts − abgewählte Basis-Zutaten (per Name).
//   Bun    = Rezept von customizations.bun.id   ?? Default-Bun.
//   Patty  = Rezept von customizations.patty.id ?? (nicht gestrichen ? Default-Patty : keins).
//   + Dressing/Side/Extras/Sauces/SideSauces = Rezepte der jeweiligen Options-IDs.
// Danach IDENTISCHE Aggregation wie bei einem Gericht (_aggregate) → SQL+JS deckungsgleich.

/**
 * Default-Optionen eines Gerichts (Bun/Patty) aus `menu_items.meta` — Spiegel
 * von `dish_default_options` (phase_114 + S156c phase_116). Nur für meta.isBurger.
 *   Bun   = Option mit name = meta.defaultBun (S156c) ?? isDefault ?? erste (displayOrder).
 *   Patty = Option mit name = meta.defaultPatty.
 * @param {{meta:object}} menuItem - das Gericht (Burger)
 * @param {Array} menu - alle menu_items (für Options-Auflösung)
 * @returns {{bun:(string|null), patty:(string|null)}} Options-menu_item-IDs
 */
export function defaultOptionsFor(menuItem, menu) {
  const out = { bun: null, patty: null };
  const meta = (menuItem && menuItem.meta) || {};
  if (!meta.isBurger) return out;
  const opts = (Array.isArray(menu) ? menu : [])
    .filter(m => m.itemType === 'option' && !m.deletedAt);
  if (meta.bunGroup) {
    const buns = opts.filter(m => m.meta && m.meta.optionGroup === meta.bunGroup)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    let def = null;
    if (meta.defaultBun) def = buns.find(b => b.name === meta.defaultBun) || null; // S156c
    if (!def) def = buns.find(b => b.meta && b.meta.isDefault) || buns[0];
    if (def) out.bun = def.id;
  }
  if (meta.pattyGroup && meta.defaultPatty) {
    const p = opts.filter(m => m.meta && m.meta.optionGroup === meta.pattyGroup && m.name === meta.defaultPatty)
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))[0];
    if (p) out.patty = p.id;
  }
  return out;
}

function _entryFromRi(ri, kind) {
  return {
    ingredientId: ri.ingredientId,
    quantity: ri.quantity != null ? ri.quantity : null,
    unit: ri.unit || 'base',
    isOptional: !!ri.isOptional,
    sortOrder: ri.sortOrder || 0,
    stepNo: (ri.stepNo != null ? ri.stepNo : null),   // S154 Bauanleitung
    stepText: ri.stepText || null,
    station: ri.station || null,
    kind
  };
}

/**
 * Effektive Zutaten-Zeilen einer Bestellposition als getaggte Liste — gemeinsamer
 * Helfer für Nährwerte (A2) UND Bauanleitung (S154/C). VOLL MODULAR:
 *   Basis-Zeilen (kind 'base'), abgewählte Basis-Zeilen MIT als kind 'remove'
 *   (für „ohne …"), Bun/Patty (gewählt ?? Default) als 'base', Dressing 'base',
 *   Side/Extras/Sauces/SideSauces als 'add'. `__patty__`-Streichung → synthetische
 *   'remove'-Zeile (kein Rezept). Reihenfolge offen (Konsument sortiert).
 * @returns {Array<{ingredientId,quantity,unit,isOptional,sortOrder,stepNo,stepText,station,kind,name?,rankHint?}>}
 */
export function composeLines(baseMenuItemId, customizations, ctx = {}) {
  const c = customizations || {};
  const recipesByItem = ctx.recipesByItem || {};
  const ingredientsById = ctx.ingredientsById || {};
  const defaults = ctx.defaults || { bun: null, patty: null };
  // S154c — für die Bauanleitung gehören Beilage (side) + Dip-Soßen (sideSauces)
  // NICHT in den Stapel; der Nährwert-Pfad (forBuild=false) behält sie.
  const forBuild = !!ctx.forBuild;
  const removed = new Set((Array.isArray(c.removedIngredients) ? c.removedIngredients : [])
    .map(x => String(x).toLowerCase()));
  const pattyStruck = removed.has('__patty__');
  const entries = [];

  // Basis: behaltene + (per Name) abgewählte Zeilen (letztere als 'remove')
  for (const ri of (recipesByItem[baseMenuItemId] || [])) {
    if (!ri) continue;
    const ing = ingredientsById[ri.ingredientId];
    const isRemoved = ing && removed.has(String(ing.name || '').toLowerCase());
    entries.push(_entryFromRi(ri, isRemoved ? 'remove' : 'base'));
  }
  const pushOpt = (id, kind) => { for (const ri of (recipesByItem[id] || [])) if (ri) entries.push(_entryFromRi(ri, kind)); };

  // Bun (base)
  const bunId = (c.bun && c.bun.id) || defaults.bun;
  if (bunId) pushOpt(bunId, 'base');
  // Patty (base) bzw. synthetische Streichung
  let pattyId = (c.patty && c.patty.id) || null;
  if (!pattyId && !pattyStruck) pattyId = defaults.patty;
  if (pattyId) pushOpt(pattyId, 'base');
  else if (pattyStruck) entries.push({ ingredientId: null, name: 'Patty', quantity: null, unit: null, isOptional: false, sortOrder: 0, stepNo: null, stepText: null, station: null, kind: 'remove', rankHint: 20 });
  // Dressing (base, In-Gericht — z. B. Bowl-Dressing). Bleibt auch im Bau.
  if (c.dressing && c.dressing.id) pushOpt(c.dressing.id, 'base');
  // Extras = Toppings AM Gericht (extra Käse/Bacon) → bleiben im Bau.
  (Array.isArray(c.extras) ? c.extras : []).forEach(e => e && e.id && pushOpt(e.id, 'add'));
  // S154c — „dazu"-Soßen/Dips + Beilage gehören NICHT in die Bauanleitung (Dip-Cups
  // neben dem Gericht, nicht eingebaut; In-Gericht-Soßen stehen im Basis-Rezept).
  // Der Nährwert-Pfad (forBuild=false) behält sie (der Gast isst sie mit).
  if (!forBuild) {
    // S229 T1 — Mehrfach-Beilagen (sides[]) tolerant, Legacy-side (Objekt) als Fallback.
    const sidesArr = Array.isArray(c.sides) ? c.sides : (c.side ? [c.side] : []);
    sidesArr.forEach(x => x && x.id && pushOpt(x.id, 'add'));
    (Array.isArray(c.sauces) ? c.sauces : []).forEach(e => e && e.id && pushOpt(e.id, 'add'));
    (Array.isArray(c.sideSauces) ? c.sideSauces : []).forEach(e => e && e.id && pushOpt(e.id, 'add'));
  }
  return entries;
}

/**
 * Komponierte Nährwerte einer echten Bestellposition. Spiegel von `compose_nutrition`.
 * Nutzt `composeLines` (gemeinsam mit der Bauanleitung) und aggregiert alle nicht
 * abgewählten Zeilen mit Zutat (`_aggregate` überspringt optionale + fehlende).
 * @param {string} baseMenuItemId - Gericht-ID (Basis-Rezept)
 * @param {object} customizations - { bun:{id}, patty:{id}, dressing:{id},
 *                                    sides:[{id}] (bzw. Legacy side:{id}),
 *                                    extras:[{id}], sauces:[{id}], sideSauces:[{id}],
 *                                    removedIngredients:[<name>] }
 * @param {object} ctx - { recipesByItem, ingredientsById, namedUnitsByIngredient?, defaults? }
 * @returns {object} gleiche Form wie computeNutrition
 */
export function composeNutrition(baseMenuItemId, customizations, ctx = {}) {
  const entries = composeLines(baseMenuItemId, customizations, ctx);
  const forNutrition = entries.filter(e => e.kind !== 'remove' && e.ingredientId);
  return _aggregate(forNutrition, ctx.ingredientsById || {}, ctx.namedUnitsByIngredient || {});
}
